import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { inventoryApi } from '@/api/clients/apiClient'
import { useOrgStore } from '@/stores/orgStore'
import type { Inventory, InventoriesPage } from '@/api/types'
import { InventoryModal } from '@/components/common/InventoryModal'

function formatDate(iso?: string) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

// ── Inventories Page ──────────────────────────────────────────────────────────

export default function Inventories() {
  const { currentOrgId } = useOrgStore()
  const queryClient = useQueryClient()

  const [showModal, setShowModal] = useState(false)
  const [editTarget, setEditTarget] = useState<Inventory | null>(null)
  const [search, setSearch] = useState('')

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['inventories', currentOrgId],
    queryFn: () => inventoryApi.list(currentOrgId),
  })
  const inventories = data?.inventories ?? []

  const deleteMutation = useMutation({
    mutationFn: (id: string) => inventoryApi.delete(currentOrgId, id),
    onSuccess: (_, deletedId) => {
      queryClient.setQueryData<InventoriesPage>(['inventories', currentOrgId], (old) =>
        old
          ? { ...old, inventories: old.inventories.filter((i) => i.id !== deletedId), total: old.total - 1 }
          : old
      )
    },
  })

  const filtered = inventories.filter((inv) =>
    !search || inv.name.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div className="space-y-6" id="inventories-page">
      {showModal && (
        <InventoryModal orgId={currentOrgId} onClose={() => setShowModal(false)} />
      )}
      {editTarget && (
        <InventoryModal
          initial={editTarget}
          orgId={currentOrgId}
          onClose={() => setEditTarget(null)}
        />
      )}

      {/* Error */}
      {isError && (
        <div className="flex items-start gap-3 p-4 bg-error/5 dark:bg-error/10 border border-error/20 dark:border-error/25 rounded-xl">
          <span className="material-icons text-error text-xl mt-0.5">error_outline</span>
          <div className="flex-1">
            <p className="text-sm font-medium text-error">Failed to load inventories</p>
            <p className="text-sm text-error/80 mt-0.5 font-mono">
              {(error as Error)?.message ?? 'Unknown error'}
            </p>
          </div>
          <button onClick={() => refetch()} className="text-sm text-error hover:underline shrink-0">
            Retry
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-light dark:text-text-dark">Inventories</h1>
          <p className="text-sm text-subtle-light dark:text-subtle-dark mt-0.5">
            {inventories.length} inventor{inventories.length !== 1 ? 'ies' : 'y'} configured
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 text-sm font-medium"
        >
          <span className="material-icons text-base">add</span>
          New Inventory
        </button>
      </div>

      {/* Filter bar */}
      <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] p-4 rounded-xl shadow-ambient dark:shadow-none border border-border-light dark:border-border-dark">
        <div className="flex flex-wrap gap-3">
          <input
            type="text"
            placeholder="Search inventories…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="px-3 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary/50 w-56"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] rounded-xl shadow-ambient dark:shadow-none border border-border-light dark:border-border-dark overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-container-light dark:bg-container-dark">
              <tr>
                <th className="px-6 py-3 text-left label-sm text-subtle-light dark:text-subtle-dark">Name</th>
                <th className="px-6 py-3 text-left label-sm text-subtle-light dark:text-subtle-dark">Sources</th>
                <th className="px-6 py-3 text-left label-sm text-subtle-light dark:text-subtle-dark">Created</th>
                <th className="px-6 py-3 text-right label-sm text-subtle-light dark:text-subtle-dark">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={4} className="px-6 py-10 text-center">
                    <span className="material-icons animate-spin text-primary text-2xl">sync</span>
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-10 text-center text-subtle-light dark:text-subtle-dark">
                    {inventories.length === 0 ? (
                      <div className="flex flex-col items-center gap-2">
                        <span className="material-icons text-4xl opacity-40">inventory_2</span>
                        <p>No inventories configured yet.</p>
                        <button
                          onClick={() => setShowModal(true)}
                          className="text-primary hover:underline text-sm"
                        >
                          Create your first inventory
                        </button>
                      </div>
                    ) : (
                      'No inventories match the current filters.'
                    )}
                  </td>
                </tr>
              ) : (
                filtered.map((inv) => (
                  <tr
                    key={inv.id}
                    className="border-t border-border-light dark:border-border-dark hover:bg-background-light dark:hover:bg-background-dark"
                  >
                    <td className="px-6 py-3 max-w-sm">
                      <Link
                        to={`/inventories/${inv.id}`}
                        className="font-medium text-primary dark:text-secondary hover:underline line-clamp-1"
                      >
                        {inv.name}
                      </Link>
                      {inv.description && (
                        <p className="text-xs text-subtle-light dark:text-subtle-dark line-clamp-1 mt-0.5">{inv.description}</p>
                      )}
                      {inv.tags?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {inv.tags.map((t) => (
                            <span key={t} className="px-2 py-0.5 text-xs rounded-full bg-primary/10 dark:bg-secondary/15 text-primary dark:text-secondary border border-primary/20 dark:border-secondary/25 font-medium">
                              {t}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-3 text-subtle-light dark:text-subtle-dark">
                      {inv.source_count}
                    </td>
                    <td className="px-6 py-3 text-subtle-light dark:text-subtle-dark text-xs">
                      {formatDate(inv.created_at)}
                    </td>
                    <td className="px-6 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <Link
                          to={`/inventories/${inv.id}`}
                          title="View Details"
                          className="p-1.5 rounded-md text-subtle-light dark:text-subtle-dark hover:text-primary hover:bg-primary/10"
                        >
                          <span className="material-icons text-base">open_in_new</span>
                        </Link>
                        <button
                          title="Edit"
                          onClick={() => setEditTarget(inv)}
                          className="p-1.5 rounded-md text-subtle-light dark:text-subtle-dark hover:text-primary hover:bg-primary/10"
                        >
                          <span className="material-icons text-base">edit</span>
                        </button>
                        <button
                          title="Delete"
                          onClick={() => {
                            if (confirm(`Delete inventory "${inv.name}"?`)) {
                              deleteMutation.mutate(inv.id)
                            }
                          }}
                          className="p-1.5 rounded-md text-subtle-light dark:text-subtle-dark hover:text-error hover:bg-error/10"
                        >
                          <span className="material-icons text-base">delete</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
